import React, { useState, useEffect } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Badge } from '@roger/ui-components';
import { Switch } from '@roger/ui-components';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  Loader2,
  RefreshCw,
  Edit,
  AlertTriangle,
  Check,
  Plus,
  Trash2,
  Search,
} from 'lucide-react';
import {
  getAgencyInsurances,
  getAgencyClinics,
  syncClinicsFromClinicInfo,
  createAgencyClinic,
  createAgencyInsurance,
  updateAgencyInsurance,
  deleteAgencyInsurance,
  searchPayors,
  type AgencyInsuranceResponse,
  type AgencyClinicResponse,
  type PayorSearchResult,
} from '../services/clinicsApi';
import { updateAgencyClinicAgencyClinicsAgencyClinicIdPut } from '../services/api/sdk.gen';
import { client } from '../services/api';
import { US_STATES } from '../constants/options';

type ClinicEditForm = {
  name?: string;
  npi?: string;
  ein?: string;
  address?: string;
  city?: string;
  state?: string | null;
  zip_code?: string;
  phone?: string;
  hha_agency_id?: string;
};

const Admin: React.FC = () => {
  const { user, showSuccess, showError, hasAnyPermission } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasAnyPermission: state.hasAnyPermission,
    }))
  );

  // Clinic management state
  const [isLoadingClinicInfo, setIsLoadingClinicInfo] = useState(false);

  // Clinics list state
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
  const [clinicModalMode, setClinicModalMode] = useState<'create' | 'edit'>(
    'edit'
  );
  const [editingClinicId, setEditingClinicId] = useState<string | null>(null);
  const [editingClinicHasKey, setEditingClinicHasKey] = useState(false);
  const [clinicEditForm, setClinicEditForm] = useState<ClinicEditForm>({});
  const [isSavingClinic, setIsSavingClinic] = useState(false);

  // Insurance list state
  const [insurances, setInsurances] = useState<AgencyInsuranceResponse[]>([]);
  const [isLoadingInsurances, setIsLoadingInsurances] = useState(false);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [clinicFilter, setClinicFilter] = useState<string>('all');

  // Insurance create/edit modal state
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
  const [insuranceModalMode, setInsuranceModalMode] = useState<
    'create' | 'edit'
  >('create');
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  const [insuranceForm, setInsuranceForm] = useState<{
    agency_clinic_id: string;
    name: string;
    edi_payer_id: string;
    edi_clearinghouse: string;
    visit_authorization_required: boolean;
    noa_required: boolean;
  }>({
    agency_clinic_id: '',
    name: '',
    edi_payer_id: '',
    edi_clearinghouse: '',
    visit_authorization_required: false,
    noa_required: false,
  });
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingInsuranceId, setDeletingInsuranceId] = useState<string | null>(
    null
  );
  const [isDeletingInsurance, setIsDeletingInsurance] = useState(false);

  // Payor lookup state
  const [payorSearchQuery, setPayorSearchQuery] = useState('');
  const [payorSearchResults, setPayorSearchResults] = useState<
    PayorSearchResult[]
  >([]);
  const [isSearchingPayors, setIsSearchingPayors] = useState(false);
  const [showPayorResults, setShowPayorResults] = useState(false);

  const isKinnserEnabled = user?.agency?.is_kinnser_enabled ?? false;
  const isRogerEmrEnabled = user?.agency?.is_rogeremr_enabled ?? false;

  // Load clinics
  const loadClinics = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingClinics(true);
    try {
      const data = await getAgencyClinics(user.agency.id);
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showError('Failed to load clinic data');
    } finally {
      setIsLoadingClinics(false);
    }
  };

  // Load insurances
  const loadInsurances = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingInsurances(true);
    try {
      const data = await getAgencyInsurances(user.agency.id);
      setInsurances(data);
    } catch (error) {
      console.error('Failed to load insurances:', error);
      showError('Failed to load insurance data');
    } finally {
      setIsLoadingInsurances(false);
    }
  };

  // Load clinics on mount
  useEffect(() => {
    if (user?.agency?.id) {
      loadClinics();
      loadInsurances();
    }
  }, [user?.agency?.id]);

  // Permission check - placed after all hooks to comply with Rules of Hooks
  if (!hasAnyPermission(['external-admin:access', 'emr:manage'])) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Admin features. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle get clinic info
  const handleSyncClinicInfo = async () => {
    if (!user?.agency?.system_name) {
      showError('No agency system name found');
      return;
    }

    setIsLoadingClinicInfo(true);

    try {
      // Sync clinic info to database (this now fetches from RogerDoc API internally)
      const syncResult = await syncClinicsFromClinicInfo();
      showSuccess(
        `Clinic and insurance information synced successfully. ` +
          `Clinics: ${syncResult.clinics.created_count} created, ${syncResult.clinics.updated_count} updated. ` +
          `Insurances: ${syncResult.insurances.created_count} created, ${syncResult.insurances.updated_count} updated.`
      );
      await loadClinics(); // Reload clinics after successful sync
      await loadInsurances(); // Reload insurances after successful sync
    } catch (error) {
      console.error('Failed to get clinic info:', error);

      // Scraping disabled - show error instead
      showError(
        'Failed to retrieve clinic information. Scraping is currently disabled.'
      );
    } finally {
      setIsLoadingClinicInfo(false);
    }
  };

  // Handle opening create clinic dialog
  const handleCreateClinic = () => {
    setClinicModalMode('create');
    setEditingClinicId(null);
    setEditingClinicHasKey(false);
    setClinicEditForm({ name: '' });
    setIsClinicModalOpen(true);
  };

  // Normalize EIN to digits-only (Stedi rejects hyphens/other chars)
  const normalizeEin = (value: string) => value.replace(/\D/g, '');

  // Handle opening edit clinic dialog
  const handleEditClinic = (clinic: AgencyClinicResponse) => {
    setClinicModalMode('edit');
    setEditingClinicId(clinic.id);
    setEditingClinicHasKey(!!clinic.clinic_key);
    setClinicEditForm({
      name: clinic.name || '',
      npi: clinic.npi || '',
      ein: normalizeEin(clinic.ein || ''),
      address: clinic.address || '',
      city: clinic.city || '',
      state: clinic.state || '',
      zip_code: clinic.zip_code || '',
      phone: clinic.phone || '',
      hha_agency_id: clinic.hha_agency_id || '',
    });
    setIsClinicModalOpen(true);
  };

  // Handle clinic save (create or update)
  const handleSaveClinic = async () => {
    if (clinicModalMode === 'create') {
      if (!clinicEditForm.name?.trim()) {
        showError('Clinic name is required');
        return;
      }
      if (!user?.agency?.id) return;

      setIsSavingClinic(true);
      try {
        await createAgencyClinic({
          agency_id: user.agency.id,
          name: clinicEditForm.name.trim(),
          npi: clinicEditForm.npi || undefined,
          ein: normalizeEin(clinicEditForm.ein || '') || undefined,
          address: clinicEditForm.address || undefined,
          city: clinicEditForm.city || undefined,
          state: clinicEditForm.state || undefined,
          zip_code: clinicEditForm.zip_code || undefined,
          phone: clinicEditForm.phone || undefined,
          hha_agency_id: clinicEditForm.hha_agency_id || undefined,
        });
        showSuccess('Clinic created successfully');
        setIsClinicModalOpen(false);
        setClinicEditForm({});
        await loadClinics();
      } catch (error) {
        console.error('Failed to create clinic:', error);
        showError('Failed to create clinic');
      } finally {
        setIsSavingClinic(false);
      }
    } else {
      if (!editingClinicId) return;

      setIsSavingClinic(true);
      try {
        await updateAgencyClinicAgencyClinicsAgencyClinicIdPut({
          client,
          path: { agency_clinic_id: editingClinicId },
          body: {
            name: clinicEditForm.name || undefined,
            npi: clinicEditForm.npi || undefined,
            ein: normalizeEin(clinicEditForm.ein || '') || undefined,
            address: clinicEditForm.address || undefined,
            city: clinicEditForm.city || undefined,
            state: clinicEditForm.state || undefined,
            zip_code: clinicEditForm.zip_code || undefined,
            phone: clinicEditForm.phone || undefined,
            hha_agency_id: clinicEditForm.hha_agency_id || undefined,
          },
        });
        showSuccess('Clinic updated successfully');
        setIsClinicModalOpen(false);
        setEditingClinicId(null);
        setClinicEditForm({});
        await loadClinics();
      } catch (error) {
        console.error('Failed to update clinic:', error);
        showError('Failed to update clinic');
      } finally {
        setIsSavingClinic(false);
      }
    }
  };

  // Handle clinic cancel
  const handleCancelEdit = () => {
    setIsClinicModalOpen(false);
    setEditingClinicId(null);
    setEditingClinicHasKey(false);
    setClinicEditForm({});
  };

  // Clinics created in Roger EMR (no clinic_key) for insurance creation
  const rogerEmrClinics = clinics.filter(c => !c.clinic_key);
  const canAddInsurance = isRogerEmrEnabled && rogerEmrClinics.length > 0;

  // Insurance modal handlers
  const handleCreateInsurance = () => {
    setInsuranceModalMode('create');
    setEditingInsuranceId(null);
    setInsuranceForm({
      agency_clinic_id: '',
      name: '',
      edi_payer_id: '',
      edi_clearinghouse: '',
      visit_authorization_required: false,
      noa_required: false,
    });
    setPayorSearchQuery('');
    setPayorSearchResults([]);
    setShowPayorResults(false);
    setIsInsuranceModalOpen(true);
  };

  const handleEditInsurance = (insurance: AgencyInsuranceResponse) => {
    setInsuranceModalMode('edit');
    setEditingInsuranceId(insurance.id);
    setInsuranceForm({
      agency_clinic_id: '',
      name: insurance.name,
      edi_payer_id: insurance.edi_payer_id || '',
      edi_clearinghouse: insurance.edi_clearinghouse || '',
      visit_authorization_required:
        insurance.visit_authorization_required ?? false,
      noa_required: insurance.noa_required ?? false,
    });
    setPayorSearchQuery('');
    setPayorSearchResults([]);
    setShowPayorResults(false);
    setIsInsuranceModalOpen(true);
  };

  const handleSaveInsurance = async () => {
    if (insuranceModalMode === 'create') {
      if (!insuranceForm.agency_clinic_id) {
        showError('Please select a clinic');
        return;
      }
      if (!insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      setIsSavingInsurance(true);
      try {
        await createAgencyInsurance({
          agency_clinic_id: insuranceForm.agency_clinic_id,
          name: insuranceForm.name.trim(),
          edi_payer_id: insuranceForm.edi_payer_id.trim() || undefined,
          edi_clearinghouse:
            insuranceForm.edi_clearinghouse.trim() || undefined,
          visit_authorization_required:
            insuranceForm.visit_authorization_required,
          noa_required: insuranceForm.noa_required,
        });
        showSuccess('Insurance created successfully');
        setIsInsuranceModalOpen(false);
        await loadInsurances();
      } catch (error) {
        console.error('Failed to create insurance:', error);
        showError('Failed to create insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    } else {
      if (!editingInsuranceId) return;
      if (!insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      setIsSavingInsurance(true);
      try {
        await updateAgencyInsurance(editingInsuranceId, {
          name: insuranceForm.name.trim(),
          edi_payer_id: insuranceForm.edi_payer_id.trim() || null,
          edi_clearinghouse: insuranceForm.edi_clearinghouse.trim() || null,
          visit_authorization_required:
            insuranceForm.visit_authorization_required,
          noa_required: insuranceForm.noa_required,
        });
        showSuccess('Insurance updated successfully');
        setIsInsuranceModalOpen(false);
        await loadInsurances();
      } catch (error) {
        console.error('Failed to update insurance:', error);
        showError('Failed to update insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    }
  };

  const handleCancelInsuranceModal = () => {
    setIsInsuranceModalOpen(false);
    setEditingInsuranceId(null);
    setInsuranceForm({
      agency_clinic_id: '',
      name: '',
      edi_payer_id: '',
      edi_clearinghouse: '',
      visit_authorization_required: false,
      noa_required: false,
    });
    setPayorSearchQuery('');
    setPayorSearchResults([]);
    setShowPayorResults(false);
  };

  const handleDeleteInsurance = (insuranceId: string) => {
    setDeletingInsuranceId(insuranceId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteInsurance = async () => {
    if (!deletingInsuranceId) return;

    setIsDeletingInsurance(true);
    try {
      await deleteAgencyInsurance(deletingInsuranceId);
      showSuccess('Insurance deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeletingInsuranceId(null);
      await loadInsurances();
    } catch (error) {
      console.error('Failed to delete insurance:', error);
      showError('Failed to delete insurance. Please try again.');
    } finally {
      setIsDeletingInsurance(false);
    }
  };

  const handlePayorSearch = async () => {
    if (!payorSearchQuery.trim()) return;

    setIsSearchingPayors(true);
    setShowPayorResults(true);
    try {
      const results = await searchPayors(payorSearchQuery.trim());
      setPayorSearchResults(results);
    } catch (error) {
      console.error('Failed to search payors:', error);
      setPayorSearchResults([]);
    } finally {
      setIsSearchingPayors(false);
    }
  };

  const handleSelectPayor = (payor: PayorSearchResult) => {
    setInsuranceForm(prev => ({
      ...prev,
      name: payor.payor_name,
      edi_payer_id: payor.electronic_payor_id || '',
    }));
    setShowPayorResults(false);
    setPayorSearchQuery('');
  };

  // Filtered insurances
  const filteredInsurances = insurances.filter(insurance => {
    const matchesSearch =
      insuranceSearch === '' ||
      insurance.name.toLowerCase().includes(insuranceSearch.toLowerCase()) ||
      insurance.edi_payer_id
        .toLowerCase()
        .includes(insuranceSearch.toLowerCase()) ||
      insurance.clinic_name
        .toLowerCase()
        .includes(insuranceSearch.toLowerCase());

    const matchesClinic =
      clinicFilter === 'all' || insurance.agency_clinic_id === clinicFilter;

    return matchesSearch && matchesClinic;
  });

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Admin Panel</h1>
        <p className='text-muted-foreground'>
          Administrative functions and system configuration.
        </p>
      </div>

      <div className='grid gap-6'>
        {/* Clinic Management Section */}
        <Card>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle>Clinic Management</CardTitle>
              </div>
              <div className='flex gap-2'>
                {isRogerEmrEnabled && (
                  <Button onClick={handleCreateClinic} variant='outline'>
                    <Plus className='mr-2 h-4 w-4' />
                    Add Clinic
                  </Button>
                )}
                {isKinnserEnabled && (
                  <Button
                    onClick={handleSyncClinicInfo}
                    disabled={isLoadingClinicInfo}
                    variant='outline'
                  >
                    {isLoadingClinicInfo ? (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    ) : (
                      <RefreshCw className='mr-2 h-4 w-4' />
                    )}
                    Sync Clinic Info
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Clinic Name</TableHead>
                      {isKinnserEnabled && <TableHead>Clinic Key</TableHead>}
                      <TableHead>NPI</TableHead>
                      <TableHead>EIN</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Phone</TableHead>
                      {isKinnserEnabled && (
                        <>
                          <TableHead>Clinicians</TableHead>
                          <TableHead>Case Managers</TableHead>
                          <TableHead>Branches</TableHead>
                        </>
                      )}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingClinics ? (
                      <TableRow>
                        <TableCell
                          colSpan={isKinnserEnabled ? 10 : 7}
                          className='text-center py-8'
                        >
                          <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                          <p className='text-sm text-muted-foreground mt-2'>
                            Loading clinics...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : clinics.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={isKinnserEnabled ? 10 : 7}
                          className='text-center py-8 text-muted-foreground'
                        >
                          {user?.agency?.id
                            ? isKinnserEnabled
                              ? 'No clinics found. Try syncing clinic info first.'
                              : 'No clinics found. Click "Add Clinic" to create one.'
                            : 'No agency found. Please ensure you are logged in with an agency.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      clinics.map(clinic => (
                        <TableRow key={clinic.id}>
                          <TableCell className='font-medium'>
                            {clinic.name ||
                              (clinic.clinic_key && clinics.length === 1 ? (
                                <span className='text-muted-foreground italic'>
                                  Default Clinic
                                </span>
                              ) : (
                                '—'
                              ))}
                          </TableCell>
                          {isKinnserEnabled && (
                            <TableCell className='text-sm text-muted-foreground'>
                              {clinic.clinic_key || '—'}
                            </TableCell>
                          )}
                          <TableCell>
                            {clinic.npi ? (
                              <Badge variant='secondary'>{clinic.npi}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {clinic.ein ? (
                              <Badge variant='outline'>{clinic.ein}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell className='text-sm'>
                            {clinic.address && (
                              <div>
                                {clinic.address}
                                {clinic.city &&
                                  clinic.state &&
                                  clinic.zip_code && (
                                    <div className='text-muted-foreground'>
                                      {clinic.city}, {clinic.state}{' '}
                                      {clinic.zip_code}
                                    </div>
                                  )}
                              </div>
                            )}
                            {!clinic.address && (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {clinic.phone ? (
                              <span className='text-sm'>{clinic.phone}</span>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          {isKinnserEnabled && (
                            <>
                              <TableCell>
                                <Badge variant='outline'>
                                  {clinic.clinicians?.length || 0}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant='outline'>
                                  {clinic.case_managers?.length || 0}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant='outline'>
                                  {clinic.branches?.length || 0}
                                </Badge>
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => handleEditClinic(clinic)}
                            >
                              <Edit className='h-4 w-4' />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Create/Edit Modal */}
        <Dialog open={isClinicModalOpen} onOpenChange={setIsClinicModalOpen}>
          <DialogContent className='sm:max-w-[600px]'>
            <DialogHeader>
              <DialogTitle>
                {clinicModalMode === 'create' ? 'Add Clinic' : 'Edit Clinic'}
              </DialogTitle>
              <DialogDescription>
                {clinicModalMode === 'create'
                  ? 'Create a new clinic for your agency.'
                  : 'Update clinic information.'}
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4'>
              <div className='grid gap-2'>
                <label htmlFor='name' className='text-sm font-medium'>
                  Clinic Name{clinicModalMode === 'create' && ' *'}
                </label>
                <Input
                  id='name'
                  value={clinicEditForm.name || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  disabled={clinicModalMode === 'edit' && editingClinicHasKey}
                />
                {clinicModalMode === 'edit' && editingClinicHasKey && (
                  <p className='text-xs text-muted-foreground'>
                    Name is synced from an external source and cannot be edited.
                  </p>
                )}
              </div>
              <div className='grid gap-2'>
                <label htmlFor='npi' className='text-sm font-medium'>
                  NPI
                </label>
                <Input
                  id='npi'
                  value={clinicEditForm.npi || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      npi: e.target.value,
                    }))
                  }
                  maxLength={10}
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='ein' className='text-sm font-medium'>
                  EIN
                </label>
                <Input
                  id='ein'
                  value={clinicEditForm.ein || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      ein: normalizeEin(e.target.value),
                    }))
                  }
                  placeholder='Digits only (no hyphens)'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='phone' className='text-sm font-medium'>
                  Phone
                </label>
                <Input
                  id='phone'
                  value={clinicEditForm.phone || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='hha_agency_id' className='text-sm font-medium'>
                  HHA Agency ID (Medicare Provider Number)
                </label>
                <Input
                  id='hha_agency_id'
                  value={clinicEditForm.hha_agency_id || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      hha_agency_id: e.target.value,
                    }))
                  }
                  placeholder='e.g. 067890'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='address' className='text-sm font-medium'>
                  Address
                </label>
                <Input
                  id='address'
                  value={clinicEditForm.address || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div className='grid gap-2'>
                  <label htmlFor='city' className='text-sm font-medium'>
                    City
                  </label>
                  <Input
                    id='city'
                    value={clinicEditForm.city || ''}
                    onChange={e =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className='grid gap-2'>
                  <label htmlFor='state' className='text-sm font-medium'>
                    State
                  </label>
                  <Select
                    value={clinicEditForm.state || 'none'}
                    onValueChange={value =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        state: value === 'none' ? null : value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None</SelectItem>
                      {US_STATES.map(state => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className='grid gap-2'>
                <label htmlFor='zip_code' className='text-sm font-medium'>
                  ZIP Code
                </label>
                <Input
                  id='zip_code'
                  value={clinicEditForm.zip_code || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      zip_code: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveClinic}
                disabled={
                  isSavingClinic ||
                  (clinicModalMode === 'create' && !clinicEditForm.name?.trim())
                }
              >
                {isSavingClinic && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                {clinicModalMode === 'create'
                  ? 'Create Clinic'
                  : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Insurance Providers Section */}
        <Card>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle>Insurance Providers</CardTitle>
                <CardDescription>
                  {isRogerEmrEnabled
                    ? 'Manage insurance providers configured for your agency'
                    : 'View insurance providers configured for your agency'}
                </CardDescription>
              </div>
              {isRogerEmrEnabled && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          onClick={handleCreateInsurance}
                          variant='outline'
                          disabled={!canAddInsurance}
                        >
                          <Plus className='mr-2 h-4 w-4' />
                          Add Insurance
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canAddInsurance && (
                      <TooltipContent>
                        <p>
                          You need to create a clinic first before adding
                          insurance
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex flex-col lg:flex-row gap-4'>
              <div className='flex-1'>
                <Input
                  placeholder='Search insurances...'
                  value={insuranceSearch}
                  onChange={e => setInsuranceSearch(e.target.value)}
                  className='w-full'
                />
              </div>
              <div className='w-full lg:w-64'>
                <Select value={clinicFilter} onValueChange={setClinicFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder='Filter by clinic' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Clinics</SelectItem>
                    {clinics
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(clinic => (
                        <SelectItem key={clinic.id} value={clinic.id}>
                          {clinic.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={loadInsurances}
                disabled={isLoadingInsurances}
                variant='outline'
              >
                {isLoadingInsurances ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
              </Button>
            </div>

            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Insurance Name</TableHead>
                    <TableHead>Payer ID</TableHead>
                    <TableHead>Clearinghouse</TableHead>
                    <TableHead>Clinic</TableHead>
                    <TableHead>Insurance Key</TableHead>
                    <TableHead>Payor Mapped</TableHead>
                    <TableHead>Prior Auth Required</TableHead>
                    <TableHead>NOA Required</TableHead>
                    <TableHead>Eligibility Enabled</TableHead>
                    {isRogerEmrEnabled && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingInsurances ? (
                    <TableRow>
                      <TableCell
                        colSpan={isRogerEmrEnabled ? 9 : 8}
                        className='text-center py-8'
                      >
                        <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                        <p className='text-sm text-muted-foreground mt-2'>
                          Loading insurances...
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : filteredInsurances.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={isRogerEmrEnabled ? 9 : 8}
                        className='text-center py-8 text-muted-foreground'
                      >
                        {insurances.length === 0
                          ? isRogerEmrEnabled
                            ? 'No insurance providers found. Click "Add Insurance" to create one.'
                            : 'No insurance providers found. Try updating from clinic info first.'
                          : 'No insurances match your search criteria.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInsurances.map(insurance => (
                      <TableRow key={insurance.id}>
                        <TableCell className='font-medium'>
                          {insurance.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant='secondary'>
                            {insurance.edi_payer_id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {insurance.edi_clearinghouse ? (
                            <Badge variant='outline'>
                              {insurance.edi_clearinghouse}
                            </Badge>
                          ) : (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell>{insurance.clinic_name}</TableCell>
                        <TableCell className='text-sm text-muted-foreground'>
                          {insurance.insurance_key || '—'}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.payor_id ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.visit_authorization_required ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.noa_required ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.stedi_eligibility_enabled ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                        {isRogerEmrEnabled && (
                          <TableCell>
                            {!insurance.clinic_key ? (
                              <div className='flex gap-1'>
                                <Button
                                  size='sm'
                                  variant='outline'
                                  onClick={() => handleEditInsurance(insurance)}
                                >
                                  <Edit className='h-4 w-4' />
                                </Button>
                                <Button
                                  size='sm'
                                  variant='outline'
                                  onClick={() =>
                                    handleDeleteInsurance(insurance.id)
                                  }
                                  className='text-red-600 hover:text-red-700 hover:bg-red-50'
                                >
                                  <Trash2 className='h-4 w-4' />
                                </Button>
                              </div>
                            ) : null}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Insurance Create/Edit Modal */}
        {isRogerEmrEnabled && (
          <Dialog
            open={isInsuranceModalOpen}
            onOpenChange={setIsInsuranceModalOpen}
          >
            <DialogContent className='sm:max-w-[750px]'>
              <DialogHeader>
                <DialogTitle>
                  {insuranceModalMode === 'create'
                    ? 'Add Insurance'
                    : 'Edit Insurance'}
                </DialogTitle>
                <DialogDescription>
                  {insuranceModalMode === 'create'
                    ? 'Create a new insurance provider for a clinic.'
                    : 'Update insurance provider details.'}
                </DialogDescription>
              </DialogHeader>
              <div className='grid grid-cols-2 gap-6 py-4'>
                {/* Left: form fields */}
                <div className='grid gap-4 content-start'>
                  {insuranceModalMode === 'create' && (
                    <div className='grid gap-2'>
                      <label className='text-sm font-medium'>Clinic *</label>
                      <Select
                        value={insuranceForm.agency_clinic_id || 'placeholder'}
                        onValueChange={value =>
                          setInsuranceForm(prev => ({
                            ...prev,
                            agency_clinic_id:
                              value === 'placeholder' ? '' : value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select a clinic' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='placeholder' disabled>
                            Select a clinic
                          </SelectItem>
                          {rogerEmrClinics.map(clinic => (
                            <SelectItem key={clinic.id} value={clinic.id}>
                              {clinic.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className='grid gap-2'>
                    <label className='text-sm font-medium'>
                      Insurance Name *
                    </label>
                    <Input
                      value={insuranceForm.name}
                      onChange={e =>
                        setInsuranceForm(prev => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder='e.g. Aetna'
                    />
                  </div>

                  <div className='grid gap-2'>
                    <label className='text-sm font-medium'>Payer ID</label>
                    <Input
                      value={insuranceForm.edi_payer_id}
                      onChange={e =>
                        setInsuranceForm(prev => ({
                          ...prev,
                          edi_payer_id: e.target.value,
                        }))
                      }
                      placeholder='e.g. 60054'
                    />
                  </div>

                  <div className='grid gap-2'>
                    <label className='text-sm font-medium'>Clearinghouse</label>
                    <Input
                      value={insuranceForm.edi_clearinghouse}
                      onChange={e =>
                        setInsuranceForm(prev => ({
                          ...prev,
                          edi_clearinghouse: e.target.value,
                        }))
                      }
                      placeholder='Optional'
                    />
                  </div>

                  <div className='flex items-center gap-3'>
                    <Switch
                      checked={insuranceForm.visit_authorization_required}
                      onCheckedChange={checked =>
                        setInsuranceForm(prev => ({
                          ...prev,
                          visit_authorization_required: checked,
                        }))
                      }
                    />
                    <label className='text-sm font-medium'>
                      Authorization Required
                    </label>
                  </div>

                  <div className='flex items-center gap-3'>
                    <Switch
                      checked={insuranceForm.noa_required}
                      onCheckedChange={checked =>
                        setInsuranceForm(prev => ({
                          ...prev,
                          noa_required: checked,
                        }))
                      }
                    />
                    <label className='text-sm font-medium'>NOA Required</label>
                  </div>
                </div>

                {/* Right: Stedi payor lookup */}
                <div className='flex flex-col gap-2 border-l pl-6'>
                  <label className='text-sm font-medium'>
                    Lookup from Payer Database
                  </label>
                  <div className='flex gap-2'>
                    <Input
                      placeholder='Search by name or ID...'
                      value={payorSearchQuery}
                      onChange={e => setPayorSearchQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handlePayorSearch();
                        }
                      }}
                      className='flex-1'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      onClick={handlePayorSearch}
                      disabled={isSearchingPayors || !payorSearchQuery.trim()}
                    >
                      {isSearchingPayors ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Search className='h-4 w-4' />
                      )}
                    </Button>
                  </div>
                  {showPayorResults && (
                    <div className='min-h-0 max-h-[250px] flex-1 overflow-y-auto rounded-md border'>
                      {payorSearchResults.length === 0 ? (
                        <div className='p-3 text-sm text-muted-foreground text-center'>
                          {isSearchingPayors
                            ? 'Searching...'
                            : 'No payors found'}
                        </div>
                      ) : (
                        payorSearchResults.map(payor => (
                          <button
                            key={payor.id}
                            type='button'
                            className='w-full text-left px-3 py-2 hover:bg-accent text-sm border-b last:border-b-0 cursor-pointer'
                            onClick={() => handleSelectPayor(payor)}
                          >
                            <div className='font-medium'>
                              {payor.payor_name}
                            </div>
                            <div className='text-muted-foreground text-xs'>
                              {payor.electronic_payor_id && (
                                <span>
                                  Payer ID: {payor.electronic_payor_id}
                                </span>
                              )}
                              {payor.payor_type && (
                                <span className='ml-2'>
                                  ({payor.payor_type})
                                </span>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={handleCancelInsuranceModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveInsurance}
                  disabled={
                    isSavingInsurance ||
                    !insuranceForm.name.trim() ||
                    (insuranceModalMode === 'create' &&
                      !insuranceForm.agency_clinic_id)
                  }
                >
                  {isSavingInsurance && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  {insuranceModalMode === 'create'
                    ? 'Create Insurance'
                    : 'Save Changes'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Insurance Delete Confirmation Dialog */}
        {isRogerEmrEnabled && (
          <Dialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
          >
            <DialogContent className='sm:max-w-[400px]'>
              <DialogHeader>
                <DialogTitle>Delete Insurance</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this insurance provider? This
                  action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant='outline'
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setDeletingInsuranceId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant='destructive'
                  onClick={confirmDeleteInsurance}
                  disabled={isDeletingInsurance}
                >
                  {isDeletingInsurance && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default Admin;
